import React, {useMemo, useReducer, useRef} from 'react';
import {useSelector} from 'react-redux';
import {ControlGroup, InputGroup} from '@blueprintjs/core';

import JSONEditor from '../../JSONEditor';
import {blockResultSelector} from '../../../store/reducers/blockResultReducer';
import {contentReducer, setContentFormKeyValue, setPreview, unsetPreview} from '../store';
import {BaseBlock, ContentOptions, ContentActions} from '../types';
import {defaultContentActions, defaultContentOptions} from '../constants';
import {aceEditorReadOnly, shouldLoadResult, toggleAceReadOnly} from '../utils';
import {initialApiRequestContentState} from './store';
import {useCreateBlock, useExecuteRequest, useInitBlock, useLoadResult, useUpdateBlockContentJson} from '../hooks';
import {createDefaultContent} from './utils';
import {ApiResult, ContentCard, ContentToolbar} from '../components';
import {SimpleSelect} from '../../select';
import {METHOD_OPTIONS} from './constants';

import './ApiRequest.scss';

interface ApiRequestProps {
  content: BaseBlock;
  options?: ContentOptions;
  actions?: ContentActions;
}

export const ApiRequest = ({
  content,
  options = defaultContentOptions,
  actions = defaultContentActions,
}: ApiRequestProps) => {
  const [contentState, contentDispatch] = useReducer(contentReducer, initialApiRequestContentState);
  options = useMemo((): ContentOptions => ({...defaultContentOptions, ...options}), [options?.readOnly]);
  actions = useMemo((): ContentActions => ({...defaultContentActions, ...actions}), []);

  const editorRef = useRef<any>();
  const uid = content.uid;
  const result = useSelector(blockResultSelector(uid as string, content.result_id as string));
  const resultId: string | null = content.result_id || result?.key || null;
  const defaultValue = useMemo(() => JSON.stringify(contentState.form.value, null, 2), []);

  const {updating, updateBlockContentJson} = useUpdateBlockContentJson(uid, options.readOnly, options.autosave);
  const {executing, executeRequest} = useExecuteRequest(uid);

  useCreateBlock(content, createDefaultContent());
  useInitBlock(uid, content, contentDispatch);
  useLoadResult(uid, resultId,
    options.loadResult !== undefined ? options.loadResult : shouldLoadResult(content.result_id, result)
  );

  const onExecute = () => {
    const block: any = {
      content_json: {
        method: contentState.form.method,
        url: contentState.form.url,
        value: contentState.form.value,
      }
    };
    executeRequest(block);
  };

  const onMethodChange = (value: any) => {
    const method = value.value;
    updateBlockContentJson('method', method);
    contentDispatch(setContentFormKeyValue('method', method));
  };

  const onUrlChange = (event: any) => {
    if (contentState.isPreview) {
      return;
    }

    const value = event.target.value;
    updateBlockContentJson('url', value);
    contentDispatch(setContentFormKeyValue('url', value));
  };

  const onValueChange = (value: string) => {
    if (aceEditorReadOnly(editorRef.current?.editor)) {
      return;
    }

    let newValue = contentState.form.value;
    try {
      newValue = JSON.parse(value);
    } catch (e) {
      // silent fail
    }
    updateBlockContentJson('value', newValue);
    contentDispatch(setContentFormKeyValue('value', newValue));
  };

  const onPreview = (data: any) => {
    const aceEditor = editorRef.current?.editor || null;

    if (!aceEditor) {
      return;
    }

    if (!data) {
      contentDispatch(unsetPreview());
      toggleAceReadOnly(aceEditor, null, false);
    } else {
      try {
        const jsonData = JSON.parse(data);

        contentDispatch(setPreview(jsonData));
        toggleAceReadOnly(aceEditor, JSON.stringify(jsonData.value, null, 2), true);
      } catch (e: any) {
        toggleAceReadOnly(aceEditor, e.message, true);
      }
    }
  }

  return (
    <ContentCard
      key={content.id}
      type={content.type}
      contentId={contentState.init ? uid : null}
      disabled={options.readOnly}
      noContentElement={undefined}
      allowCollapse={options.allowCollapse}
      showHeader={options.showHeader}
      showFooter={options.showFooter}
    >
      <div className="content__api-request">
        <ContentToolbar
          show={options?.showToolbar}
          contentId={uid as string}
          executing={executing}
          onExecute={options.showRun ? onExecute : null}
          updating={updating}
          onDelete={actions.onDelete}
          showDelete={options.showDelete}
          showPreview={options.showPreview}
          onPreview={onPreview}
          showSave={false}
          disabled={options.readOnly || false}
          showSelect={false}
        />
        <div className="content-card__content__form">
          <ControlGroup className="method-url-group">
            <SimpleSelect
              selected={contentState.form.method}
              options={METHOD_OPTIONS}
              disabled={options.readOnly || false}
              onChange={onMethodChange}
              popoverProps={{
                usePortal: true,
              }}
              buttonProps={{
                className: 'select-method-button'
              }}
            />
            <InputGroup
              value={contentState.form.url}
              onChange={onUrlChange}
              placeholder="Enter request URL"
              fill={true}
              disabled={contentState.isPreview || options.readOnly}
            />
          </ControlGroup>
          <JSONEditor
            ref={editorRef}
            defaultValue={defaultValue}
            onChange={onValueChange}
            disabled={options.readOnly}
            editorOptions={{
              maxLines: 10,
            }}
            editorProps={{
              height: '140px',
            }}
          />
          <ApiResult
            result={result}
            failedTitle="API Request run failed"
          />
        </div>
      </div>
    </ContentCard>
  );
};
